import { useState } from "react";
import { Link } from "react-router-dom";
import AvailabilitySearch from "@/components/AvailabilitySearch";
import { AirportHosts } from "@/components/AirportHosts";
import { Seo } from "@/components/Seo";
import { BELO_PROVIDER } from "@/data/cityLocations";
import {
  Plane, MapPin, ShieldCheck, Clock, CheckCircle2, ChevronDown, Phone, Sparkles,
} from "lucide-react";

const SITE = "https://belorentals.com";
const HERO_IMG = "/marketing/logan-airport.webp";

const STEPS = [
  { icon: Plane, title: "Land at Boston Logan (BOS)", body: "Fly into Logan International and skip the long rental-counter lines. Your host has your car ready for a quick, professional handoff." },
  { icon: MapPin, title: "Choose a host who offers airport service", body: "Browse Belo Rentals hosts who offer Logan Airport pickup & return, compare their vehicles, ratings and airport fee, and pick the one that fits your trip." },
  { icon: CheckCircle2, title: "Book on the host's Belo profile & meet at BOS", body: "Reserve directly on the host's Belo Rentals profile, add the airport pickup & return option at checkout, and meet at the airport to start your trip." },
];

const FAQS = [
  { q: "Do you offer car rental pickup and drop-off at Logan Airport (BOS)?", a: "Yes. Belo Rentals hosts who opt in offer Logan Airport pickup and return. Browse the hosts below, choose one, and add the airport pickup & return option when you book on their Belo Rentals profile." },
  { q: "How do I book the Logan Airport pickup & return?", a: "Pick a host from the list on this page, open their Belo Rentals profile, choose your vehicle and dates, and select 'Logan Airport pickup & return' at checkout. You book and pay securely on Belorentals.com." },
  { q: "How much does the airport pickup & return cost?", a: "Rentals start from $50/day. Each host sets their own airport pickup & return fee, shown on their card below and added to your total at checkout." },
  { q: "How far is Belo Rentals from Logan Airport?", a: "Our Malden location is about 7 miles from Boston Logan. With airport pickup & return you don't need to travel there yourself — your host coordinates the meet at the airport." },
  { q: "What do I need to rent a car after landing?", a: "A valid driver's license and a credit card. You must meet the minimum rental age, and a young-driver fee may apply for drivers under 25." },
  { q: "How does protection work?", a: "Protection details are shown before you pay — see your rental agreement for exact coverage specifics before you arrive in Boston." },
  { q: "Can I rent for a Cape Cod or New England road trip after landing?", a: "Absolutely. Travel throughout Massachusetts and New England is welcome — pick up at Logan and drive straight to Cape Cod, the coast, or wherever your trip takes you." },
];

export default function AirportDropoff() {
  const [openFaq, setOpenFaq] = useState(0);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Logan Airport Pickup & Return", item: `${SITE}/logan-airport-drop-off` },
    ],
  };
  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Airport Car Rental Pickup & Return",
    name: "Logan Airport Car Rental Pickup & Return",
    areaServed: { "@type": "City", name: "Boston, MA" },
    provider: { ...BELO_PROVIDER, image: `${SITE}${HERO_IMG}` },
  };

  return (
    <div data-testid="airport-dropoff-page" className="bg-white">
      <Seo
        title="Logan Airport Car Rental Pickup & Return, Boston (BOS) | Belo Rentals"
        description="Landing at Boston Logan? Book Logan Airport (BOS) pickup & return with Belo Rentals. Clean, well-maintained cars from $50/day — reserve online and meet at the airport."
        path="/logan-airport-drop-off"
        keywords="Logan Airport car rental, Boston airport car rental pickup, Logan Airport pickup and return, car rental Boston Logan BOS, airport car rental Boston, rent a car Boston Logan airport"
        image={`${SITE}${HERO_IMG}`}
        jsonLd={[faqJsonLd, breadcrumbLd, serviceLd]}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-5 sm:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-5xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-600/10 text-blue-700 text-sm font-medium mb-6">
            <Plane size={15} /> Logan Airport pickup &amp; return
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
            Boston Logan Airport Car Rental — Pickup &amp; Return
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Flying into Boston Logan (BOS)? Skip the counter lines. Choose a Belo Rentals host who offers
            Logan Airport pickup &amp; return and book a clean, inspected, well-maintained car — from
            <span className="font-semibold text-slate-800"> $50/day</span>.
          </p>
          <div className="mt-8 max-w-2xl mx-auto">
            <p className="text-sm text-slate-500 mb-3">Check availability for your dates:</p>
            <AvailabilitySearch />
            <p className="mt-4 text-sm text-slate-500 flex items-center justify-center gap-1.5">
              <Phone size={14} className="text-slate-400" /> Prefer to talk? Call{" "}
              <a href="tel:+19782211451" className="text-blue-600 font-semibold hover:underline">(978) 221-1451</a>
            </p>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-4">
        <img src={HERO_IMG} alt="Logan Airport (BOS) car rental pickup & return — Belo Rentals" loading="eager"
          className="w-full aspect-[21/9] object-cover rounded-3xl shadow-card" data-testid="dropoff-hero-image" />
      </div>

      {/* Airport hosts */}
      <section className="py-20 px-5 sm:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="label-caps text-blue-600">Hosts offering airport service</p>
            <h2 className="font-heading text-3xl font-bold text-slate-900 mt-3">Pick a host for your Logan Airport handoff</h2>
            <p className="text-slate-600 mt-3">These Belo Rentals hosts offer pickup &amp; return at Boston Logan. Compare their vehicles, ratings and airport fee, then book directly on their profile.</p>
          </div>
          <AirportHosts />
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-5 sm:px-8 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">How airport pickup &amp; return works</h2>
          <p className="text-slate-500 text-center mt-3 max-w-2xl mx-auto">From landing at Logan to driving away — three easy steps.</p>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {STEPS.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100">
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-blue-600 text-white mb-5"><s.icon size={22} /></span>
                <div className="text-xs font-bold text-blue-600 mb-1">STEP {i + 1}</div>
                <h3 className="font-heading text-lg font-bold text-slate-900">{s.title}</h3>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-14 px-5 sm:px-8 max-w-5xl mx-auto grid sm:grid-cols-3 gap-6 text-center">
        {[
          { icon: ShieldCheck, t: "Transparent protection", s: "Details shown before you pay." },
          { icon: Clock, t: "Fast handoff", s: "No crowded counter lines." },
          { icon: Plane, t: "Logan made easy", s: "Meet your host at the airport." },
        ].map((b, i) => (
          <div key={i} className="flex flex-col items-center">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-blue-50 text-blue-600 mb-3"><b.icon size={22} /></span>
            <div className="font-heading font-bold text-slate-900">{b.t}</div>
            <div className="text-sm text-slate-500">{b.s}</div>
          </div>
        ))}
      </section>

      {/* FAQ */}
      <section className="py-20 px-5 sm:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">Logan Airport pickup &amp; return FAQ</h2>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} data-testid={`faq-item-${i}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                  <span className="font-medium text-slate-900">{f.q}</span>
                  <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-r from-slate-900 to-blue-900 text-white text-center px-8 py-14">
          <Sparkles size={32} className="mx-auto text-blue-200" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-4">Landing at Boston Logan?</h2>
          <p className="mt-4 text-blue-100 max-w-xl mx-auto">Choose a host who offers Logan Airport pickup &amp; return, or browse the full fleet and book online.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/fleet?airport=1" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-slate-900 font-semibold hover:bg-blue-50 transition-colors">
              See airport cars
            </Link>
            <a href="tel:+19782211451" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">
              <Phone size={16} /> (978) 221-1451
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
